import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit, FiUser } from 'react-icons/fi';
import api from '../../services/api';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import { DataGrid } from '../Common/DataGrid';
import { useProject } from '../../context/ProjectContext';
import { common as commonMessages, sites as siteMessages } from '../../utils/validationMessages';
import SiteSelectionModal from './SiteSelectionModal';
import AssignCRAModal from './AssignCRAModal';
import AddNewSiteModal from './AddNewSiteModal';
import EditSiteModal from './EditSiteModal';
import './SiteFeasibilitySelection.css';
import { pickTherapeuticAreaDisplay } from '../../utils/therapeuticArea';

const SiteFeasibilitySelection = () => {
  const { currentProject: contextProject } = useProject();
  const currentProject = contextProject && contextProject !== 'all' ? contextProject : null;
  const [profilePermissions, setProfilePermissions] = useState({});
  const isViewerProfile = profilePermissions.isViewOnly === 1 || profilePermissions.IsViewOnly === 1;
  const [selectedSites, setSelectedSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showAssignCRAModal, setShowAssignCRAModal] = useState(false);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [showEditSiteModal, setShowEditSiteModal] = useState(false);
  const [selectedSiteForCRA, setSelectedSiteForCRA] = useState(null);
  const [selectedSiteForEdit, setSelectedSiteForEdit] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const fetchSelectedSites = useCallback(async () => {
    if (!currentProject) {
      setSelectedSites([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/sites/feasibility/selected?project=${currentProject}`);
      
      // Map backend response to frontend format (Site No display = vSiteCode; nSiteNo = numeric for APIs e.g. audit)
      const mappedSites = response.data.map(site => ({
        siteId: site.siteId,
        nSiteNo: site.siteNo,
        siteNo: site.vSiteCode ?? site.siteCode ?? site.siteNo ?? site.siteId,
        siteCode: site.siteCode ?? site.vSiteCode ?? '',
        projectCode: currentProject ?? site.projectCode ?? site.project ?? '',
        piName: site.piName || '',
        siteName: site.siteName || '',
        siteAddress: site.siteAddress || '',
        therapeuticArea: pickTherapeuticAreaDisplay(site),
        disease: site.disease || 'N/A',
        cra: site.cra || null,
        craList: site.craList || [],
        investigatorEmail: site.investigatorEmail || '',
        siteEmail: site.siteEmail || '',
        ecEmail: site.ecEmail || '',
        recruitmentProjections: site.recruitmentProjections || '',
        isActive: site.isActive !== undefined ? site.isActive : true,
        isInProgressStage: site.isInProgressStage === true,
        isSiteOnboarding: site.isSiteOnboarding === true,
      }));
      
      setSelectedSites(mappedSites);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.message || 
                          'Failed to load selected sites';
      setError(errorMessage);
      console.error('Error fetching selected sites:', err);
      setSelectedSites([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [currentProject]);

  useEffect(() => {
    fetchSelectedSites();
  }, [fetchSelectedSites]);

  useEffect(() => {
    let cancelled = false;
    api.get('/users/me/profile-permissions')
      .then((res) => {
        if (!cancelled && res.data) {
          setProfilePermissions(res.data);
        }
      })
      .catch(() => {
        if (!cancelled) setProfilePermissions({});
      });
    return () => { cancelled = true; };
  }, [currentProject]);

  const parseStsLpRowIdFromSiteId = (siteId) => {
    const s = String(siteId || '').trim();
    const m = /^stslp-row-(\d+)$/i.exec(s);
    return m ? parseInt(m[1], 10) : null;
  };

  const handleSiteSelected = async (payload) => {
    const isEnvelope =
      payload &&
      typeof payload === 'object' &&
      !Array.isArray(payload) &&
      Array.isArray(payload.sites);
    const sites = Array.isArray(payload) ? payload : isEnvelope ? payload.sites : [];
    const eSignRemark =
      isEnvelope && typeof payload.remark === 'string' ? payload.remark.trim() || undefined : undefined;

    if (!sites || sites.length === 0) {
      setError('Please select at least one site');
      return;
    }
    if (!currentProject) {
      setError('No project selected. Please select a project first.');
      return;
    }

    const stsLpRowIds = [
      ...new Set(
        sites.map((s) => parseStsLpRowIdFromSiteId(s.siteId)).filter((id) => id != null)
      ),
    ];
    const normalSites = sites.filter((s) => parseStsLpRowIdFromSiteId(s.siteId) == null);

    try {
      setError(null);
      const errorParts = [];

      if (stsLpRowIds.length > 0) {
        const stsRes = await api.post('/sites/feasibility/sts-lp-save', {
          projectCode: currentProject,
          stsLpRowIds,
          remark: eSignRemark || null,
        });
        const stsData = stsRes.data || {};
        const rowErrors = stsData.errors || stsData.Errors || [];
        if (rowErrors.length > 0) {
          errorParts.push(
            rowErrors
              .map((e) => e.message || e.Message || 'Row error')
              .join('; ')
          );
        }
      }

      if (normalSites.length > 0) {
        const requestData = {
          projectCode: currentProject,
          sites: normalSites.map((site) => ({
            siteId: site.siteId || site.siteNo,
            siteNo: site.siteNo || site.siteId,
          })),
        };
        await api.post('/sites/feasibility/select', requestData);
      }

      await fetchSelectedSites();

      if (errorParts.length > 0) {
        setError(errorParts.join(' '));
        return;
      }

      setToast({ type: 'success', message: commonMessages.authSuccess });
      setShowSelectionModal(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to save selected sites';
      setError(errorMessage);
      console.error('Error saving selected sites:', err);
    }
  };

  const handleAssignCRA = (site) => {
    setSelectedSiteForCRA(site);
    setShowAssignCRAModal(true);
  };

  const handleCRAAssigned = async (siteId, craIds, remark) => {
    try {
      setError(null);
      const remarkStr = typeof remark === 'string' ? remark.trim() : '';
      await api.post(`/sites/${siteId}/set-cras`, { craIds: craIds || [], remark: remarkStr });

      await fetchSelectedSites();
      setToast({ type: 'success', message: commonMessages.authSuccess });
      setShowAssignCRAModal(false);
      setSelectedSiteForCRA(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
                          err.response?.data?.error ||
                          err.message ||
                          'Failed to assign CRA';
      setError(errorMessage);
      console.error('Error assigning CRA:', err);
    }
  };

  const handleView = (site) => {
    setSelectedSiteForEdit(site);
    setShowEditSiteModal(true);
    setEditMode(false); // View mode
  };

  const handleEdit = (site) => {
    setSelectedSiteForEdit(site);
    setShowEditSiteModal(true);
    setEditMode(true); // Edit mode
  };

  const handleSiteUpdated = async (updatedSite) => {
    try {
      // Refresh the list from server to get latest data
      await fetchSelectedSites();
      setToast({ type: 'success', message: siteMessages.savedSuccess });
      setShowEditSiteModal(false);
      setSelectedSiteForEdit(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.message || 
                          'Failed to refresh sites after update';
      setError(errorMessage);
      console.error('Error refreshing sites after update:', err);
      setToast({ type: 'success', message: siteMessages.savedSuccess });
      // Even if refresh fails, update local state as fallback
      setSelectedSites(prev =>
        prev.map(site =>
          site.siteId === updatedSite.siteId ? { ...site, ...updatedSite } : site
        )
      );
      setShowEditSiteModal(false);
      setSelectedSiteForEdit(null);
    }
  };

  const handleAddNewSite = async (siteData) => {
    try {
      // The AddNewSiteModal already calls the API and creates the site
      // We just need to refresh the list to show the new site
      await fetchSelectedSites();
      setToast({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.message || 
                          'Failed to refresh sites after adding new site';
      setError(errorMessage);
      console.error('Error refreshing sites after adding new site:', err);
      // Even if refresh fails, try to add to local state as fallback
      const newSite = {
        siteId: siteData.siteId || siteData.siteNo,
        siteNo: siteData.siteNo || siteData.siteId,
        piName: siteData.piName || siteData.investigator || '',
        siteName: siteData.siteName || '',
        siteAddress: siteData.siteAddress || siteData.country || '',
        therapeuticArea: pickTherapeuticAreaDisplay(siteData),
        disease: siteData.disease || 'N/A',
        cra: null,
      };
      setSelectedSites(prev => {
        // Check if site already exists to avoid duplicates
        const existingSite = prev.find(s => s.siteId === newSite.siteId);
        if (existingSite) {
          return prev;
        }
        return [...prev, newSite];
      });
    }
  };

  if (loading) return <Spinner />;

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <div className="site-feasibility-selection">
      {toast && (
        <div style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 9999 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
      {error && (
        <Alert 
          type="error" 
          message={error} 
          onClose={handleCloseError}
        />
      )}

      {!isViewerProfile && (
        <div className="page-header flex-between" style={{ justifyContent: 'flex-end', gap: '0.75rem' }}>
          <button
            className="btn btn-primary"
            onClick={() => setShowSelectionModal(true)}
            disabled={!currentProject}
            title={!currentProject ? 'Select a project first' : 'Select sites'}
          >
            <FiPlus size={20} /> Site Selection
          </button>
        </div>
      )}

      <div className="card">
        <div className="site-feasibility-legend" aria-label="Site status color legend">
          <span className="legend-item">
            <span className="legend-swatch legend-swatch-active" />
            <span>Active</span>
          </span>
          <span className="legend-item">
            <span className="legend-swatch legend-swatch-inactive" />
            <span>Inactive</span>
          </span>
        </div>
        <DataGrid
          data={selectedSites}
          searchKeys={['siteNo', 'siteCode', 'vSiteCode', 'SiteCode', 'piName', 'siteName', 'siteAddress', 'therapeuticArea', 'disease', 'cra']}
          defaultPageSize={10}
          emptyMessage={'No sites selected. Click "Site Selection" to add sites.'}
          tableClassName="sites-table"
          columns={[
            { key: 'siteNo', label: 'Site' },
            { key: 'piName', label: 'PI Name' },
            { key: 'siteName', label: 'Site Name' },
            { key: 'siteAddress', label: 'Site Address' },
            { key: 'therapeuticArea', label: 'Therapeutic Area' },
            { key: 'disease', label: 'Disease' },
            { key: 'cra', label: 'CRA' },
            { key: 'actions', label: 'Actions' },
          ]}
          renderRow={(site) => (
            <tr key={site.siteId} className={site.isActive === false ? 'row-inactive' : undefined}>
              <td className="font-bold">{site.siteNo}</td>
              <td>{site.piName}</td>
              <td>{site.siteName}</td>
              <td>{site.siteAddress}</td>
              <td>{site.therapeuticArea}</td>
              <td>{site.disease}</td>
              <td className="text-center">
                {site.isActive === false ? (
                  site.cra ? (
                    <span className="cra-name" title={site.cra}>{site.cra}</span>
                  ) : (
                    <span className="text-muted">—</span>
                  )
                ) : site.cra ? (
                  <span className="cra-name" title={site.cra}>{site.cra}</span>
                ) : (
                  isViewerProfile ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleAssignCRA(site)}
                    >
                      <FiUser size={16} /> Assign CRA
                    </button>
                  )
                )}
              </td>
              <td className="text-center">
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleView(site)}
                    title="View Site Details"
                  >
                    View
                  </button>
                  {!isViewerProfile && (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => !site.isInProgressStage && handleEdit(site)}
                      title={site.isInProgressStage ? 'Edit is disabled when site is in In Progress stage' : 'Edit Site Details'}
                      disabled={site.isInProgressStage}
                    >
                      <FiEdit size={16} /> Edit
                    </button>
                  )}
                </div>
              </td>
            </tr>
          )}
        />
      </div>

      {/* Site Selection Modal */}
      {showSelectionModal && (
        <SiteSelectionModal
          project={currentProject}
          onClose={() => setShowSelectionModal(false)}
          onSave={handleSiteSelected}
          onAddNewSite={handleAddNewSite}
        />
      )}

      {/* Assign CRA Modal */}
      {showAssignCRAModal && selectedSiteForCRA && (
        <AssignCRAModal
          site={selectedSiteForCRA}
          projectCode={currentProject}
          onClose={() => {
            setShowAssignCRAModal(false);
            setSelectedSiteForCRA(null);
          }}
          onAssign={handleCRAAssigned}
        />
      )}

      {/* Add New Site Modal (can be opened from main page if needed) */}
      {showAddSiteModal && (
        <AddNewSiteModal
          project={currentProject}
          onClose={() => setShowAddSiteModal(false)}
          onSave={handleAddNewSite}
        />
      )}

      {/* Edit Site Modal */}
      {showEditSiteModal && selectedSiteForEdit && (
        <EditSiteModal
          site={selectedSiteForEdit}
          project={currentProject}
          initialEditMode={editMode}
          onClose={() => {
            setShowEditSiteModal(false);
            setSelectedSiteForEdit(null);
            setEditMode(false);
          }}
          onSave={handleSiteUpdated}
        />
      )}
    </div>
  );
};

export default SiteFeasibilitySelection;
